/**
 * FeedbackForm Component
 *
 * Modal or inline form for providing feedback on extracted evidence.
 * Allows users to mark items as correct or incorrect and provide corrected values.
 * Supports both evidence items and mitigations.
 *
 * Requirements: 8.2
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Checkmark20Regular,
  Dismiss20Regular,
  Edit20Regular,
  Warning20Regular,
} from '@fluentui/react-icons';
import { FeedbackSubmission } from '../../types/feedback';
import styles from './FeedbackForm.module.css';

export interface FeedbackFormProps {
  /** Unique identifier for the evidence item */
  itemId: string;
  /** Type of item being given feedback */
  itemType: 'evidence' | 'mitigation';
  /** Current extracted value being reviewed */
  currentValue: string;
  /** Callback when feedback is submitted */
  onSubmit: (feedback: FeedbackSubmission) => void;
  /** Callback when form is cancelled */
  onCancel: () => void;
  /** Optional initial feedback state for editing existing feedback */
  initialFeedback?: Partial<FeedbackSubmission>;
  /** Whether the form is in a loading/submitting state */
  isSubmitting?: boolean;
}

/**
 * FeedbackForm provides a form interface for users to provide feedback on
 * extracted evidence items. Users can mark items as correct or incorrect,
 * and when marking as incorrect, they can provide the correct value and
 * an optional comment.
 *
 * The form validates that:
 * - A correctness selection is made
 * - When marked incorrect, a corrected value is provided
 *
 * @example
 * // Basic usage
 * <FeedbackForm
 *   itemId="evidence-1"
 *   itemType="evidence"
 *   currentValue="250g"
 *   onSubmit={(feedback) => handleFeedback(feedback)}
 *   onCancel={() => setShowForm(false)}
 * />
 *
 * @example
 * // With initial feedback for editing
 * <FeedbackForm
 *   itemId="evidence-1"
 *   itemType="evidence"
 *   currentValue="250g"
 *   initialFeedback={{ isCorrect: false, correctedValue: "300g" }}
 *   onSubmit={(feedback) => handleFeedback(feedback)}
 *   onCancel={() => setShowForm(false)}
 * />
 */
export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  itemId,
  itemType,
  currentValue,
  onSubmit,
  onCancel,
  initialFeedback,
  isSubmitting = false,
}) => {
  // Form state
  const [isCorrect, setIsCorrect] = useState<boolean | null>(
    initialFeedback?.isCorrect ?? null
  );
  const [correctedValue, setCorrectedValue] = useState<string>(
    initialFeedback?.correctedValue ?? ''
  );
  const [comment, setComment] = useState<string>(
    initialFeedback?.comment ?? ''
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Refs for focus management
  const formRef = useRef<HTMLDivElement>(null);
  const correctedValueRef = useRef<HTMLTextAreaElement>(null);

  // Focus the corrected value field when marking as incorrect
  useEffect(() => {
    if (isCorrect === false && correctedValueRef.current) {
      correctedValueRef.current.focus();
    }
  }, [isCorrect]);

  // Clear validation error when user makes changes
  useEffect(() => {
    setValidationError(null);
  }, [isCorrect, correctedValue]);

  /**
   * Validates the form and returns true if valid
   */
  const validateForm = useCallback((): boolean => {
    if (isCorrect === null) {
      setValidationError('Please indicate whether the extracted value is correct');
      return false;
    }

    if (isCorrect === false && !correctedValue.trim()) {
      setValidationError('Please provide the correct value');
      return false;
    }

    return true;
  }, [isCorrect, correctedValue]);

  /**
   * Handles form submission
   */
  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();

      if (!validateForm()) {
        return;
      }

      const feedback: FeedbackSubmission = {
        itemId,
        isCorrect: isCorrect!,
        ...(isCorrect === false && { correctedValue: correctedValue.trim() }),
        ...(comment.trim() && { comment: comment.trim() }),
      };

      onSubmit(feedback);
    },
    [itemId, isCorrect, correctedValue, comment, validateForm, onSubmit]
  );

  /**
   * Handles keyboard events for accessibility
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    },
    [onCancel]
  );

  /**
   * Handles correctness selection
   */
  const handleCorrectnessChange = useCallback((correct: boolean) => {
    setIsCorrect(correct);
    if (correct) {
      setCorrectedValue('');
    }
  }, []);

  const itemTypeLabel = itemType === 'evidence' ? 'evidence item' : 'mitigation';

  return (
    <div
      ref={formRef}
      className={styles.feedbackForm}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-form-title"
      aria-describedby="feedback-form-description"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className={styles.header}>
        <h3 id="feedback-form-title" className={styles.title}>
          <Edit20Regular className={styles.titleIcon} />
          Provide Feedback
        </h3>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onCancel}
          aria-label="Close feedback form"
          disabled={isSubmitting}
        >
          <Dismiss20Regular />
        </button>
      </div>

      {/* Description */}
      <p id="feedback-form-description" className={styles.description}>
        Review the extracted value for this {itemTypeLabel} and indicate whether it is correct.
      </p>

      {/* Prototype Warning */}
      <div className={styles.prototypeWarning} role="alert">
        <Warning20Regular className={styles.prototypeWarningIcon} />
        <span>Feedback submission is not yet functional in this prototype.</span>
      </div>

      {/* Current Value Display */}
      <div className={styles.currentValueSection}>
        <label className={styles.label}>Current Extracted Value</label>
        <div className={styles.currentValue}>
          {currentValue || <span className={styles.emptyValue}>No value extracted</span>}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Correctness Selection */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Is this value correct?</legend>
          <div className={styles.radioGroup}>
            <label
              className={`${styles.radioLabel} ${isCorrect === true ? styles.selected : ''}`}
            >
              <input
                type="radio"
                name="isCorrect"
                value="true"
                checked={isCorrect === true}
                onChange={() => handleCorrectnessChange(true)}
                className={styles.radioInput}
                disabled={isSubmitting}
              />
              <span className={styles.radioButton}>
                <Checkmark20Regular className={styles.radioIcon} />
              </span>
              <span className={styles.radioText}>Yes, this is correct</span>
            </label>
            <label
              className={`${styles.radioLabel} ${isCorrect === false ? styles.selected : ''}`}
            >
              <input
                type="radio"
                name="isCorrect"
                value="false"
                checked={isCorrect === false}
                onChange={() => handleCorrectnessChange(false)}
                className={styles.radioInput}
                disabled={isSubmitting}
              />
              <span className={styles.radioButton}>
                <Warning20Regular className={styles.radioIcon} />
              </span>
              <span className={styles.radioText}>No, this needs correction</span>
            </label>
          </div>
        </fieldset>

        {/* Corrected Value Field (shown when marked incorrect) */}
        {isCorrect === false && (
          <div className={styles.fieldGroup}>
            <label htmlFor="correctedValue" className={styles.label}>
              Correct Value <span className={styles.required}>*</span>
            </label>
            <textarea
              ref={correctedValueRef}
              id="correctedValue"
              className={styles.textarea}
              value={correctedValue}
              onChange={(e) => setCorrectedValue(e.target.value)}
              placeholder="Enter the correct value..."
              rows={3}
              disabled={isSubmitting}
              aria-required="true"
              aria-invalid={validationError?.includes('correct value') ? 'true' : 'false'}
            />
          </div>
        )}

        {/* Comment Field (always shown) */}
        <div className={styles.fieldGroup}>
          <label htmlFor="comment" className={styles.label}>
            Additional Comments <span className={styles.optional}>(optional)</span>
          </label>
          <textarea
            id="comment"
            className={styles.textarea}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add any additional context or notes..."
            rows={2}
            disabled={isSubmitting}
          />
        </div>

        {/* Validation Error */}
        {validationError && (
          <div className={styles.errorMessage} role="alert">
            <Warning20Regular className={styles.errorIcon} />
            {validationError}
          </div>
        )}

        {/* Action Buttons */}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting || isCorrect === null}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FeedbackForm;
